import { useState } from "react";
import { Mail, Phone, MapPin, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Mail,
    title: "Email Us",
    value: "tracey.e.dent@gmail.com",
    description: "We'll respond within 24 hours"
  },
  {
    icon: Phone,
    title: "Call Us",
    value: "414-502-7296",
    description: "Mon-Fri, 9am-5pm CST"
  },
  {
    icon: MapPin,
    title: "Location",
    value: "Milwaukee, Wisconsin",
    description: "Serving the Milwaukee community"
  }
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="min-h-[70vh] flex items-center justify-center py-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Send className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
            Message Sent!
          </h1>
          <p className="text-lg text-slate-600 mb-8">
            Thank you for reaching out. We'll get back to you within 24 hours.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: "", email: "", subject: "", message: "" });
            }} 
            className="btn-primary"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Get in Touch
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions about our programs or want to get involved? We'd love to hear from you. Reach out and let's start a conversation about building peace together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => (
              <div 
                key={index} 
                className="bg-[#F0F9FF] p-6 rounded-2xl text-center hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-[#0056D2] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <info.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">{info.title}</h3>
                <p className="text-[#0056D2] font-medium mb-1">{info.value}</p>
                <p className="text-sm text-slate-500">{info.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-2">Send a Message</h2>
              <p className="text-slate-600 mb-8">Fill out the form below and we'll get back to you as soon as possible.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      className="mt-1 h-12 rounded-lg"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="mt-1 h-12 rounded-lg"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="How can we help?"
                    className="mt-1 h-12 rounded-lg"
                    required
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Your Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your inquiry..."
                    className="mt-1 rounded-lg resize-none"
                    rows={6}
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary h-14 text-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Additional Info */}
            <div>
              <div className="bg-[#0F172A] rounded-3xl p-8 mb-8 text-white">
                <h3 className="text-xl font-bold font-['Outfit'] mb-4">Office Hours</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#0056D2]" />
                    <div>
                      <p className="font-medium">Monday - Friday</p>
                      <p className="text-slate-400">9:00 AM - 5:00 PM CST</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-[#0056D2]" />
                    <div>
                      <p className="font-medium">Saturday</p>
                      <p className="text-slate-400">10:00 AM - 2:00 PM CST</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="font-medium text-slate-400">Sunday</p>
                      <p className="text-slate-500">Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#F0F9FF] rounded-3xl p-8">
                <h3 className="text-xl font-bold font-['Outfit'] text-[#0F172A] mb-4">Quick Response</h3>
                <p className="text-slate-600 mb-4">
                  We strive to respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full mt-2"></div>
                    <span className="text-slate-600">General inquiries: 24-48 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full mt-2"></div>
                    <span className="text-slate-600">Volunteer applications: 3-5 business days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-2 h-2 bg-[#0056D2] rounded-full mt-2"></div>
                    <span className="text-slate-600">Partnership requests: 5-7 business days</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="bg-slate-100 h-64 flex items-center justify-center">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-500">Milwaukee, Wisconsin</p>
        </div>
      </section>
    </div>
  );
}
